<script setup lang="ts">
import { computed } from 'vue';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@lucide/vue';
import { pluralize } from '@yana/shared';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Страницы под списком — одни на все разделы.
 *
 * Показывает не только «вперёд-назад», но и где человек находится:
 * «21–40 из 829». Без этого числа страницы бесполезны — непонятно ни
 * сколько всего, ни далеко ли до конца. Раньше на чёрном списке было
 * видно двести записей из восьмисот двадцати девяти, и ничто на экране
 * не говорило, что остальные существуют.
 *
 * Ширины постоянные, числа табличными цифрами — по правилу проекта:
 * иначе подпись прыгает при каждом переходе, а кнопки уезжают из-под
 * курсора ровно тогда, когда по ним щёлкают несколько раз подряд.
 */

const props = defineProps<{
  page: number;
  pages: number;
  from: number;
  to: number;
  total: number;
  hasPrev: boolean;
  hasNext: boolean;
  /** Слово для счётчика: «запись», «правка», «строка» */
  noun?: [string, string, string];
  pageSize?: number;
  pageSizes?: number[];
  loading?: boolean;
}>();

const emit = defineEmits<{
  prev: [];
  next: [];
  goTo: [page: number];
  'update:pageSize': [size: number];
}>();

const SIZES = [25, 50, 100, 200];

const counter = computed(() => {
  if (props.total === 0) return 'Ничего не найдено';
  const words = props.noun ?? ['запись', 'записи', 'записей'];
  return `${props.from}–${props.to} из ${pluralize(props.total, words[0], words[1], words[2])}`;
});
</script>

<template>
  <div
    class="@container flex min-h-11 min-w-0 shrink-0 flex-wrap items-center gap-x-2 gap-y-1 border-t border-border bg-card px-3 py-1.5 text-[11px] text-muted-foreground sm:px-4"
  >
    <span class="mr-auto min-w-0 tabular-nums">{{ counter }}</span>

    <!-- Размер страницы: подпись постоянная, значение внутри меню -->
    <DropdownMenu v-if="pageSize">
      <DropdownMenuTrigger as-child>
        <Button variant="ghost" size="sm" class="h-8 w-[76px] shrink-0 justify-between px-2 text-[11px] text-muted-foreground" aria-label="Записей на странице">
          <span class="tabular-nums">По {{ pageSize }}</span>
          <ChevronDownIcon class="size-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" class="w-32">
        <DropdownMenuRadioGroup
          :model-value="String(pageSize)"
          @update:model-value="(v) => emit('update:pageSize', Number(v))"
        >
          <DropdownMenuRadioItem
            v-for="size in pageSizes ?? SIZES"
            :key="size"
            :value="String(size)"
            class="tabular-nums"
          >
            По {{ size }}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>

    <div class="flex shrink-0 items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        class="size-8 text-muted-foreground"
        :disabled="!hasPrev || loading"
        aria-label="Предыдущая страница"
        @click="emit('prev')"
      >
        <ChevronLeftIcon class="size-4" />
      </Button>

      <!-- Ширина постоянная: «1 из 9» и «12 из 34» не должны двигать кнопки -->
      <span class="hidden w-16 shrink-0 text-center tabular-nums @[420px]:inline">{{ page }} из {{ pages }}</span>

      <Button
        variant="ghost"
        size="icon"
        class="size-8 text-muted-foreground"
        :disabled="!hasNext || loading"
        aria-label="Следующая страница"
        @click="emit('next')"
      >
        <ChevronRightIcon class="size-4" />
      </Button>
    </div>
  </div>
</template>
